/**
 * Chat companion ("电子宠物") — a tiny mood-emitting character that
 * keeps the chat surface from feeling sterile when it's empty or
 * waiting for the first token. Purely client-side: the pet has no
 * backend representation, so all state lives in `localStorage` and
 * reacts via a `storage` event + a same-tab custom event.
 *
 * Plumbing:
 *   - `readPetSettings()` / `writePetSettings()` are cheap, sync,
 *     and SSR-safe (return defaults when `window` is missing).
 *   - `usePetSettings()` subscribes to changes from both other tabs
 *     (`storage` events) and from the settings form in the same tab
 *     (a custom `knode:pet-changed` event the writer fires).
 */
import { useEffect, useState } from "react";

export type PetId =
  | "off"
  | "cat"
  | "dog"
  | "fox"
  | "hamster"
  | "rabbit"
  | "panda"
  | "ghost"
  | "robot"
  | "lemon";

export interface PetDefinition {
  id: Exclude<PetId, "off">;
  label: string;
  emoji: string;
  /** Friendly line shown in the empty chat state. */
  greeting: string;
  /** Line shown while waiting for the first token. */
  thinking: string;
  /** Tailwind color class used for the soft halo behind the pet. */
  haloClass: string;
}

export const PETS: PetDefinition[] = [
  {
    id: "cat",
    label: "Cat",
    emoji: "🐱",
    greeting: "Meow! Tap a key to start.",
    thinking: "Watching the cursor blink…",
    haloClass: "bg-amber-200/60 dark:bg-amber-300/20",
  },
  {
    id: "dog",
    label: "Dog",
    emoji: "🐶",
    greeting: "Woof! Let's chat.",
    thinking: "Fetching ideas…",
    haloClass: "bg-orange-200/60 dark:bg-orange-300/20",
  },
  {
    id: "fox",
    label: "Fox",
    emoji: "🦊",
    greeting: "Sly hello — what shall we explore?",
    thinking: "Sniffing around…",
    haloClass: "bg-orange-300/55 dark:bg-orange-400/20",
  },
  {
    id: "hamster",
    label: "Hamster",
    emoji: "🐹",
    greeting: "Squeak! Cheeks are full of ideas.",
    thinking: "Spinning the wheel…",
    haloClass: "bg-yellow-200/60 dark:bg-yellow-300/20",
  },
  {
    id: "rabbit",
    label: "Rabbit",
    emoji: "🐰",
    greeting: "Hop hop! Got a question?",
    thinking: "Digging tunnels of thought…",
    haloClass: "bg-pink-200/55 dark:bg-pink-300/20",
  },
  {
    id: "panda",
    label: "Panda",
    emoji: "🐼",
    greeting: "Bamboo break — or a quick chat?",
    thinking: "Munching bamboo…",
    haloClass: "bg-emerald-200/55 dark:bg-emerald-300/20",
  },
  {
    id: "ghost",
    label: "Ghost",
    emoji: "👻",
    greeting: "Boo! I'm hovering nearby.",
    thinking: "Drifting through the æther…",
    haloClass: "bg-violet-200/55 dark:bg-violet-300/20",
  },
  {
    id: "robot",
    label: "Robot",
    emoji: "🤖",
    greeting: "Beep boop. How can I help?",
    thinking: "Computing…",
    haloClass: "bg-sky-200/55 dark:bg-sky-300/20",
  },
  {
    id: "lemon",
    label: "Lemon",
    emoji: "🍋",
    greeting: "Zesty! Squeeze out a question.",
    thinking: "Steeping ideas…",
    haloClass: "bg-[hsl(var(--lemon))]/35",
  },
];

export interface PetSettings {
  id: PetId;
  /** Optional nickname; rendered in the speech bubble before the line. */
  name: string;
}

export const DEFAULT_PET_SETTINGS: PetSettings = { id: "cat", name: "" };

const PET_KEY = "knode.chat.pet";
const PET_NAME_KEY = "knode.chat.pet.name";
const PET_CHANGED_EVENT = "knode:pet-changed";

const VALID_IDS: ReadonlySet<PetId> = new Set<PetId>(["off", ...PETS.map((p) => p.id)]);

function isPetId(v: string | null): v is PetId {
  return v != null && VALID_IDS.has(v as PetId);
}

export function readPetSettings(): PetSettings {
  if (typeof window === "undefined") return DEFAULT_PET_SETTINGS;
  try {
    const id = localStorage.getItem(PET_KEY);
    const name = localStorage.getItem(PET_NAME_KEY) ?? "";
    return {
      id: isPetId(id) ? id : DEFAULT_PET_SETTINGS.id,
      name: name.slice(0, 24),
    };
  } catch {
    return DEFAULT_PET_SETTINGS;
  }
}

export function writePetSettings(next: PetSettings): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PET_KEY, next.id);
    localStorage.setItem(PET_NAME_KEY, next.name.slice(0, 24));
    window.dispatchEvent(new CustomEvent<PetSettings>(PET_CHANGED_EVENT, { detail: next }));
  } catch {
    /* localStorage may be disabled — settings simply don't persist */
  }
}

export function getPetDefinition(id: PetId): PetDefinition | null {
  if (id === "off") return null;
  return PETS.find((p) => p.id === id) ?? null;
}

/**
 * React hook: returns the current pet settings, kept in sync with
 * `localStorage` writes from this tab (via the custom event) and
 * from other tabs (via the native `storage` event).
 */
export function usePetSettings(): PetSettings {
  const [settings, setSettings] = useState<PetSettings>(DEFAULT_PET_SETTINGS);

  useEffect(() => {
    setSettings(readPetSettings());
    const onStorage = (e: StorageEvent): void => {
      if (e.key === PET_KEY || e.key === PET_NAME_KEY) {
        setSettings(readPetSettings());
      }
    };
    const onCustom = (e: Event): void => {
      const ce = e as CustomEvent<PetSettings>;
      if (ce.detail) setSettings(ce.detail);
      else setSettings(readPetSettings());
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(PET_CHANGED_EVENT, onCustom as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(PET_CHANGED_EVENT, onCustom as EventListener);
    };
  }, []);

  return settings;
}
